import { useMemo, useState } from "react";
import {
  ArrowTopRightOnSquareIcon,
  CheckIcon,
  ClipboardDocumentIcon,
  CodeBracketIcon,
} from "@heroicons/react/24/outline";
import {
  buildEmailSignatureHtml,
  buildEmailSignatureText,
  isValidSignatureEmail,
  type SignatureDetails,
} from "./signatureBuilder";

const initialDetails: SignatureDetails = {
  name: "",
  role: "",
  email: "",
  phone: "",
  website: "bookburrow.uk",
  showLogo: true,
};

const fieldClass = "mt-2 w-full rounded-xl border border-forest-800/15 bg-white px-4 py-3 text-sm text-bark-900 shadow-sm outline-none transition-colors placeholder:text-bark-600/45 focus:border-forest-600 focus:ring-2 focus:ring-forest-600/10";

export function EmailSignature() {
  const [details, setDetails] = useState<SignatureDetails>(initialDetails);
  const [copied, setCopied] = useState<"rich" | "html" | null>(null);
  const [copyError, setCopyError] = useState("");
  const html = useMemo(() => buildEmailSignatureHtml(details), [details]);
  const text = useMemo(() => buildEmailSignatureText(details), [details]);
  const canCopy = Boolean(details.name.trim()) && isValidSignatureEmail(details.email);

  function update<Key extends keyof SignatureDetails>(key: Key, value: SignatureDetails[Key]) {
    setDetails((current) => ({ ...current, [key]: value }));
    setCopied(null);
    setCopyError("");
  }

  async function copyRichSignature() {
    try {
      if (!navigator.clipboard?.write || typeof ClipboardItem === "undefined") {
        throw new Error("Rich clipboard is unavailable in this browser. Try the HTML copy button instead.");
      }
      await navigator.clipboard.write([
        new ClipboardItem({
          "text/html": new Blob([html], { type: "text/html" }),
          "text/plain": new Blob([text], { type: "text/plain" }),
        }),
      ]);
      setCopied("rich");
      setCopyError("");
    } catch (error) {
      setCopyError(error instanceof Error ? error.message : "The clipboard could not be updated.");
    }
  }

  async function copyHtml() {
    try {
      await navigator.clipboard.writeText(html);
      setCopied("html");
      setCopyError("");
    } catch {
      setCopyError("The clipboard could not be updated. Check your browser's clipboard permission.");
    }
  }

  return (
    <main className="min-h-[calc(100vh-5rem)] bg-cream-50">
      <section className="border-b border-forest-800/10 bg-[radial-gradient(circle_at_top_right,#e1e9d9,transparent_48%),linear-gradient(110deg,#fcfcf8,#f5f1e6)] px-6 py-14 md:py-20">
        <div className="mx-auto max-w-6xl">
          <span className="editorial-label text-forest-700">Bookburrow studio / 01</span>
          <h1 className="mt-5 max-w-3xl font-display text-4xl font-medium tracking-tight text-forest-950 sm:text-5xl md:text-6xl">
            Make every sign-off feel <em className="text-forest-600">like Bookburrow.</em>
          </h1>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-bark-600 md:text-lg">
            A tidy, readable email signature with the little rabbit, your contact details, and a touch of forest green.
          </p>
        </div>
      </section>

      <section className="mx-auto grid max-w-6xl gap-8 px-6 py-10 lg:grid-cols-[minmax(290px,380px)_minmax(0,1fr)] lg:gap-12 lg:py-14">
        <div className="rounded-3xl border border-forest-800/10 bg-white p-6 shadow-sm sm:p-8">
          <div className="mb-7 flex items-center gap-3">
            <span className="flex size-8 items-center justify-center rounded-full bg-forest-800 font-display text-sm text-white">1</span>
            <h2 className="font-display text-2xl text-forest-950">Your details</h2>
          </div>
          <div className="space-y-5">
            <label className="block text-sm font-medium text-bark-800">
              Name <span className="text-gold-700">*</span>
              <input className={fieldClass} value={details.name} onChange={(event) => update("name", event.target.value)} placeholder="Julia Smith" autoComplete="name" />
            </label>
            <label className="block text-sm font-medium text-bark-800">
              Role <span className="font-normal text-bark-600">(optional)</span>
              <input className={fieldClass} value={details.role} onChange={(event) => update("role", event.target.value)} placeholder="Founder" autoComplete="organization-title" />
            </label>
            <label className="block text-sm font-medium text-bark-800">
              Email <span className="text-gold-700">*</span>
              <input className={fieldClass} type="email" value={details.email} onChange={(event) => update("email", event.target.value)} placeholder="you@bookburrow.uk" autoComplete="email" aria-invalid={Boolean(details.email && !isValidSignatureEmail(details.email))} />
            </label>
            <label className="block text-sm font-medium text-bark-800">
              Phone <span className="font-normal text-bark-600">(optional)</span>
              <input className={fieldClass} type="tel" value={details.phone} onChange={(event) => update("phone", event.target.value)} placeholder="+44 ..." autoComplete="tel" />
            </label>
            <label className="block text-sm font-medium text-bark-800">
              Website <span className="font-normal text-bark-600">(optional)</span>
              <input className={fieldClass} value={details.website} onChange={(event) => update("website", event.target.value)} placeholder="bookburrow.uk" inputMode="url" />
            </label>
            <label className="flex cursor-pointer items-center gap-3 rounded-xl border border-forest-800/10 bg-forest-50 px-4 py-3 text-sm font-medium text-bark-800">
              <input className="size-4 accent-forest-700" type="checkbox" checked={details.showLogo} onChange={(event) => update("showLogo", event.target.checked)} />
              Include the Bookburrow rabbit
            </label>
          </div>
          <p className="mt-6 text-xs leading-relaxed text-bark-600">Your details stay in this browser while you use the page. Nothing is submitted to the website.</p>
        </div>

        <div className="min-w-0">
          <div className="mb-5 flex items-center gap-3">
            <span className="flex size-8 items-center justify-center rounded-full bg-forest-800 font-display text-sm text-white">2</span>
            <h2 className="font-display text-2xl text-forest-950">Preview & copy</h2>
          </div>
          <div className="overflow-hidden rounded-3xl border border-forest-800/10 bg-white shadow-[0_20px_60px_-40px_rgba(25,67,32,0.4)]">
            <div className="flex items-center gap-2 border-b border-forest-800/10 bg-cream-100 px-6 py-4">
              <span className="size-2.5 rounded-full bg-gold-500" />
              <span className="size-2.5 rounded-full bg-forest-400" />
              <span className="ml-3 text-xs font-medium uppercase tracking-widest text-bark-600">New message preview</span>
            </div>
            <div className="border-b border-forest-800/10 px-6 py-6 text-sm leading-7 text-bark-700 sm:px-10">
              <p>Hello,</p>
              <p className="mt-3">Thanks for getting in touch. I’ll come back to you shortly.</p>
              <p className="mt-4">Best,</p>
            </div>
            <div className="overflow-x-auto px-6 py-8 sm:px-10 sm:py-10" dangerouslySetInnerHTML={{ __html: html }} />
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            <button type="button" disabled={!canCopy} onClick={copyRichSignature} className="inline-flex items-center gap-2 rounded-xl bg-forest-800 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-forest-700 disabled:cursor-not-allowed disabled:opacity-50">
              {copied === "rich" ? <CheckIcon className="size-4" /> : <ClipboardDocumentIcon className="size-4" />}
              {copied === "rich" ? "Copied for Proton Mail" : "Copy for Proton Mail"}
            </button>
            <button type="button" disabled={!canCopy} onClick={copyHtml} className="inline-flex items-center gap-2 rounded-xl border border-forest-800/20 bg-white px-5 py-3 text-sm font-semibold text-forest-800 transition-colors hover:bg-forest-50 disabled:cursor-not-allowed disabled:opacity-50">
              {copied === "html" ? <CheckIcon className="size-4" /> : <CodeBracketIcon className="size-4" />}
              {copied === "html" ? "HTML copied" : "Copy HTML code"}
            </button>
          </div>
          <p role="status" className="mt-3 min-h-5 text-sm text-bark-600">
            {copyError || (copied === "rich" ? "Paste into Proton Mail’s signature box." : copied === "html" ? "HTML source copied to your clipboard." : !canCopy ? "Add your name and a valid email address to copy." : "The signature is ready to copy.")}
          </p>

          <div className="mt-9 rounded-2xl border border-gold-500/30 bg-gold-50 px-5 py-5 text-sm leading-relaxed text-bark-700">
            <p className="font-semibold text-bark-900">Add it to Proton Mail</p>
            <p className="mt-2">Choose <strong>Copy for Proton Mail</strong>, then open Proton Mail → Settings → Identity and addresses. Find your address and paste into its signature editor.</p>
            <a href="https://proton.me/support/display-name-email-signature" target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1 font-semibold text-forest-800 underline decoration-gold-500 underline-offset-4 hover:text-forest-600">
              Proton’s signature instructions <ArrowTopRightOnSquareIcon className="size-4" />
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
